<script lang="ts">
  import { Dialog } from 'bits-ui';
  import type { Snippet } from 'svelte';
  import { lockModalViewport } from './modal-viewport';

  let {
    open = false,
    labelledby,
    presentation = 'centered',
    dismissOnBackdrop = false,
    returnFocus,
    required = false,
    welcome = false,
    onDismiss = () => {},
    onBlurCapture,
    header,
    children,
  }: {
    open?: boolean;
    labelledby: string;
    presentation?: 'centered' | 'sheet' | 'large';
    dismissOnBackdrop?: boolean;
    returnFocus?: () => HTMLElement | null;
    required?: boolean;
    welcome?: boolean;
    onDismiss?: () => void;
    onBlurCapture?: (event: FocusEvent) => void;
    header?: Snippet;
    children?: Snippet;
  } = $props();

  let panel = $state<HTMLDivElement | null>(null);
  let opener: HTMLElement | null = null;
  let pointerOpener: HTMLElement | null = null;
  let wasOpen = false;

  function rememberPointer(event: PointerEvent): void {
    if (open) return;
    pointerOpener =
      event.target instanceof Element
        ? event.target.closest<HTMLElement>('button, a, input, select, summary')
        : null;
  }

  function handleOpenChange(next: boolean): void {
    if (next || required) return;
    onDismiss();
  }

  function handleOpenAutoFocus(event: Event): void {
    event.preventDefault();
    if (!open || !panel) return;
    const target = panel.querySelector<HTMLElement>('[autofocus]') ?? panel;
    target.focus({ preventScroll: true });
  }

  function handleCloseAutoFocus(event: Event): void {
    event.preventDefault();
    const target =
      returnFocus?.() ??
      (opener?.isConnected && !opener.matches(':disabled')
        ? opener
        : document.querySelector<HTMLElement>('.page-back:not(:disabled), #app button:not(:disabled)'));
    target?.focus({ preventScroll: true });
    opener = null;
  }

  $effect(() => {
    if (open && !wasOpen) {
      opener =
        pointerOpener ??
        (document.activeElement instanceof HTMLElement && document.activeElement !== document.body
          ? document.activeElement
          : null);
      pointerOpener = null;
    }
    wasOpen = open;
  });

  $effect(() => {
    if (!open || !panel) return;
    return lockModalViewport(panel);
  });
</script>

<svelte:window
  onpointerdowncapture={rememberPointer}
  onkeydown={() => {
    if (!open) pointerOpener = null;
  }}
/>

<Dialog.Root {open} onOpenChange={handleOpenChange}>
  <Dialog.Portal to="#app, #showcase">
    <Dialog.Overlay
      class="modal-overlay dialog-overlay"
      onclick={(event) => {
        // Bits 2.19 registers touch dismissal after a 10ms debounce, which can
        // miss a quick tap's click. Keep explicit backdrop activation reliable.
        if (event.target === event.currentTarget && dismissOnBackdrop && !required) onDismiss();
      }}
    />
    <Dialog.Content
      bind:ref={panel}
      class={`surface modal-panel dialog-panel ${presentation === 'sheet' ? 'sheet' : ''} ${presentation === 'large' ? 'large' : ''} ${welcome ? 'welcome-dialog' : ''}`}
      aria-labelledby={labelledby}
      preventScroll={false}
      onOpenAutoFocus={handleOpenAutoFocus}
      onCloseAutoFocus={handleCloseAutoFocus}
      onEscapeKeydown={(event) => {
        if (required) event.preventDefault();
      }}
      onInteractOutside={(event) => {
        if (required || !dismissOnBackdrop) event.preventDefault();
      }}
      onblurcapture={onBlurCapture}
    >
      {@render header?.()}
      <div class="modal-scroll dialog-content" class:has-header={!!header}>
        {@render children?.()}
      </div>
    </Dialog.Content>
  </Dialog.Portal>
</Dialog.Root>

<style>
  .dialog-content.has-header {
    padding-top: 0;
  }
  :global(.dialog-panel.large) .dialog-content {
    flex: 1;
  }
  :global(.dialog-panel.welcome-dialog) {
    animation: var(--theme-welcome-animation);
  }
  :global(.dialog-panel.large) {
    width: min(720px, calc(100% - 32px));
    height: calc(var(--modal-viewport-height, 100dvh) - 48px);
    max-height: calc(var(--modal-viewport-height, 100dvh) - 48px);
    --dialog-content-padding: 20px;
  }
  :global(.dialog-panel.sheet) {
    top: auto;
    margin: 0 auto;
    width: min(100%, 560px);
    max-width: 100%;
    --dialog-content-padding: 24px 26px max(24px, env(safe-area-inset-bottom));
    border-bottom: 0;
    --surface-radius: var(--theme-sheet-radius);
  }
</style>
